import { confirmSignIn, signIn, type SignInOutput } from 'aws-amplify/auth'

export type SignInOutcome =
  | { status: 'signed-in' }
  | { status: 'new-password-required' }
  | { status: 'reset-password-required' }
  | { status: 'otp-required' }
  | { status: 'totp-required' }
  | { status: 'unsupported-challenge'; step: string }
  | { status: 'failed'; message: string }

const DISABLED_MESSAGE = /user is disabled/i

const GENERIC_CREDENTIALS = 'Incorrect email or password.'

const GENERIC_OTP_REQUEST_FAILURE = 'We could not send a code to that email. Try again.'

export function messageForSignInError(error: unknown): string {
  const name = error instanceof Error ? error.name : ''
  const raw = error instanceof Error ? error.message : ''

  if (name === 'NotAuthorizedException') {
    return DISABLED_MESSAGE.test(raw) ? 'This account has been deactivated.' : GENERIC_CREDENTIALS
  }

  switch (name) {
    case 'UserNotFoundException':
      return GENERIC_CREDENTIALS
    case 'UserNotConfirmedException':
      return 'This account is not confirmed yet. Check your email for the invitation link.'
    case 'PasswordResetRequiredException':
      return 'Your password needs resetting before you can sign in.'
    case 'InvalidPasswordException':
      return 'That password does not meet the password policy.'
    case 'TooManyRequestsException':
    case 'LimitExceededException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Wait a few minutes and try again.'
    case 'EmptySignInUsername':
      return 'Enter your email address.'
    case 'EmptySignInPassword':
      return 'Enter your password.'
    case 'NetworkError':
      return 'Could not reach the sign-in service. Check your connection and try again.'
    case 'CodeMismatchException':
      return 'That code was not accepted. Request a new one and try again.'
    case 'EnableSoftwareTokenMFAException':
      return 'That code was not accepted. Check your authenticator app and try again.'
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one.'
    default:
      return 'Something went wrong signing in. Try again.'
  }
}

function outcomeFor(output: SignInOutput): SignInOutcome {
  if (output.isSignedIn) return { status: 'signed-in' }

  const step = output.nextStep.signInStep

  switch (step) {
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { status: 'new-password-required' }
    case 'RESET_PASSWORD':
      return { status: 'reset-password-required' }
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return { status: 'otp-required' }
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { status: 'totp-required' }
    default:
      return { status: 'unsupported-challenge', step }
  }
}

export async function signInWithPassword(email: string, password: string): Promise<SignInOutcome> {
  try {
    const output = await signIn({
      username: email.trim().toLowerCase(),
      password,
    })

    return outcomeFor(output)
  } catch (error) {
    if (error instanceof Error && error.name === 'UserAlreadyAuthenticatedException') {
      return { status: 'signed-in' }
    }

    return { status: 'failed', message: messageForSignInError(error) }
  }
}

export async function completeNewPassword(newPassword: string): Promise<SignInOutcome> {
  try {
    return outcomeFor(await confirmSignIn({ challengeResponse: newPassword }))
  } catch (error) {
    return { status: 'failed', message: messageForSignInError(error) }
  }
}

export async function requestEmailOtp(email: string): Promise<SignInOutcome> {
  try {
    const output = await signIn({
      username: email.trim().toLowerCase(),
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' },
    })

    return outcomeFor(output)
  } catch (error) {
    if (error instanceof Error && error.name === 'UserAlreadyAuthenticatedException') {
      return { status: 'signed-in' }
    }

    const message = messageForSignInError(error)

    return {
      status: 'failed',
      message: message === GENERIC_CREDENTIALS ? GENERIC_OTP_REQUEST_FAILURE : message,
    }
  }
}

export async function confirmEmailOtp(code: string): Promise<SignInOutcome> {
  try {
    return outcomeFor(await confirmSignIn({ challengeResponse: code }))
  } catch (error) {
    return { status: 'failed', message: messageForSignInError(error) }
  }
}

export async function confirmTotpCode(code: string): Promise<SignInOutcome> {
  try {
    return outcomeFor(await confirmSignIn({ challengeResponse: code.trim() }))
  } catch (error) {
    return { status: 'failed', message: messageForSignInError(error) }
  }
}
